import {
  AdvertisementSystem,
  ArticleBodySidebarMode,
  BlockPubArticleHeader,
  BlockPubArticlesBySectionLayout,
  BlockPubHeaderMenu,
  BlockPubHomeTopStoriesLayout,
  BlockPubNavSqueeze,
  BlockPubPromo,
  ColumnInchMethod,
  ExternalDisplay,
  ExternalParserType,
  FeedPublishStatus,
  HeaderCustomCtaType,
  IframeAspectRatio,
  LeaderboardPosition,
  OptionalPriceState,
  Paywall,
  PaywallType,
  PlanAdSettings,
  SectionLayoutStyle,
  StoryLayout,
  StripeAccountType,
  WritingRuleType,
} from '@models/types'

export const MAX_NUMBER_OF_ARTICLES_AUTHOR = 60
export const MAX_NUMBER_OF_ARTICLES_TAG = 60
export const MAX_NUMBER_OF_ARTICLES_SECTION = 60
export const MAX_NUMBER_OF_ARTICLES_LATEST_STORIES = 60
export const TOP_STORY_MAX_NB_OF_PRI_ARTICLES = 6
export const DEFAULT_PLAN_TOGGLE_TO_YEAR = true
export const DEFAULT_AD_SYSTEM: AdvertisementSystem = 'custom'
export const DEFAULT_ARTICLE_ACCESS: Paywall = 'Public'
export const DEFAULT_EXTERNAL_DISPLAY: ExternalDisplay = 'topStory'
export const DEFAULT_ARTICLE_TYPE: BlockPubArticleHeader['type'] = 'default'
export const DEFAULT_IS_IFRAME: BlockPubArticleHeader['isIframe'] = false
export const DEFAULT_HEADER_MENU_LOGO_SIZE: NonNullable<BlockPubHeaderMenu['logoSize']> = 'small'
export const DEFAULT_HEADER_LAYOUT: NonNullable<BlockPubHeaderMenu['layout']> = 'STANDARD'
export const DEFAULT_NAV_SQUEEZE_LOGO_SIZE: NonNullable<BlockPubNavSqueeze['logoSize']> = 'small'
export const PUB_HEADER_AD_HERO_DEFAULT_VISIBILITY = false
export const DEFAULT_IFRAME_EXTERNAL_DISPLAY: IframeAspectRatio = 'landscape'
export const DEFAULT_GALLERY_EXTERNAL_DISPLAY: IframeAspectRatio = 'landscape'
export const DEFAULT_GALLERY_TYPE = 'gallery'
export const DEFAULT_EREADER_INITIALIZES_FULLSCREEN = false
export const DEFAULT_EREADER_VIEW_MODE: 'single' | 'spread' = 'spread'
export const DEFAULT_EREADER_FIT_MODE: 'width' | 'page' = 'page'
export const DEFAULT_STORY_LAYOUT: StoryLayout = 'default'
export const DEFAULT_EVENT_TICKET_LABEL = 'Buy Ticket'
export const PUBLICATION_INTERNAL_PAGE_SLUGS = {
  home: '/',
  sitemap: '/sitemap/',
  subscribe: '/subscribe/',
  signIn: '/sign-in/',
  account: '/account/',
  payments: '/payments/',
  thankYou: '/thank-you/',
  terms: '/terms/',
  privacyPolicy: '/privacy-policy/',
  accessibility: '/accessibility/',
  latestStories: '/latest-stories/',
  search: '/search/',
  events: '/events/',
  contactUs: '/contact-us/',
  notFound: '/404/',
  newsletters: '/newsletters/',
  newsletterPreferences: '/newsletter-preferences/',
  tags: '/tags',
  writers: '/writers',
  sections: '/sections',
  stories: '/stories',
  allTags: '/tags/',
  allWriters: '/writers/',
  allSections: '/sections/',
  gift: '/gift/',
  giftRedeem: '/gift-redeem/',
  spotlight: '/spotlight',
  printOrderTracking: '/print-order-tracking/',
} as const

export const INTERNAL_PAGE_DROPDOWN_OPTIONS = [
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.allSections, label: 'All Sections' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.allTags, label: 'All Tags' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.allWriters, label: 'All Writers' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.contactUs, label: 'Contact Us' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.events, label: 'Events' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.gift, label: 'Gift' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.giftRedeem, label: 'Gift Redeem' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.home, label: 'Home' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.latestStories, label: 'Latest Stories' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.newsletters, label: 'Newsletters' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.privacyPolicy, label: 'Privacy Policy' },
  { key: PUBLICATION_INTERNAL_PAGE_SLUGS.terms, label: 'Terms' },
]
export const DEFAULT_SHOW_STORY_COMMENTS = false
export const DEFAULT_HIDE_WRITER_BOILERPLATE = false
export const DEFAULT_HIDE_RELATED_ARTICLES = false
export const DEFAULT_ARTICLE_BODY_SIDEBAR_MODE: ArticleBodySidebarMode = 'Related'

// Articles whose slug starts with any of these prefixes (case-insensitive) are excluded from
// the Google News sitemap. E-editions are PDF renderings of the print paper and do not belong
// in a news feed (they duplicate content already published as HTML articles and degrade the
// publication's Google News trust signal). The `BlockPubArticleHeader.section` field stores a
// block-id reference rather than a human-readable section, so we match on the URL structure
// (`/<section-slug>/<article-slug>/`) which is the canonical identity for public article URLs.
export const NEWS_SITEMAP_EXCLUDED_SLUG_PREFIXES: readonly string[] = ['/e-editions/', '/e-edition/']

// Google News requires a sitemap-news.xml to only contain articles published in the last 48h.
export const NEWS_SITEMAP_WINDOW_MS = 48 * 60 * 60 * 1000

// Schedule for the public-site revalidate cron. The stack wires this into
// `rate(${REVALIDATE_CRON_INTERVAL_MINUTES} minutes)` and the handler uses
// 2x this value as the lookback window when detecting scheduled articles
// that just crossed their `releaseDate`, so a delayed tick self-heals on
// the next run. Keep these derived from one constant so the schedule and
// the window can never drift apart.
export const REVALIDATE_CRON_INTERVAL_MINUTES = 5
export const REVALIDATE_CRON_RELEASE_LOOKBACK_MS = REVALIDATE_CRON_INTERVAL_MINUTES * 2 * 60 * 1000

// Each connected custom domain gets its own CloudFront distribution. CloudFront
// caps "distributions associated with the same cache policy" (and the same
// origin request policy) at 100, and that quota is NOT adjustable. To scale past
// ~100 domains we spread distributions across a pool of identical policies and
// pick a bucket deterministically by domain name (see cloudformation.service.ts).
// Capacity is roughly DOMAIN_POLICY_POOL_SIZE * 100 domains.
//
// The stack creates this many of EACH: server cache policy, static cache policy
// (mirrors Managed-CachingOptimized), and origin request policy (mirrors
// Managed-AllViewer). Note the account-level "Cache policies per account" (20)
// and "Origin request policies per account" (20) quotas are adjustable but must
// be raised to accommodate 2 * DOMAIN_POLICY_POOL_SIZE cache policies plus the
// main site's policy. Bump this constant (and request the quota increases) when
// approaching the capacity ceiling.
export const DOMAIN_POLICY_POOL_SIZE = 3
export const DEFAULT_LEADERBOARD_AD_POSITION: LeaderboardPosition = 'bottom'
export const MAIN_HEADER_INJECTED_SCRIPT_CONTAINER_ID = 'main-header-injected-script-container'
export const MAIN_FOOTER_INJECTED_SCRIPT_CONTAINER_ID = 'main-footer-injected-script-container'
export const NEWSLETTER_INJECTED_SCRIPT_CONTAINER_ID = 'newsletter-injected-script-container'
export const STORY_INJECTED_SCRIPT_CONTAINER_ID = 'story-injected-script-container'
export const HOME_PAGE_INJECTED_SCRIPT_CONTAINER_ID = 'home-page-injected-script-container'
export const ARTICLE_RELATED_ARTICLES_INJECTED_SCRIPT_CONTAINER_ID =
  'article-related-articles-injected-script-container'
export const EVENT_INJECTED_SCRIPT_CONTAINER_ID = 'article-events-injected-script-container'
export const TAG_HEADER_INJECTED_SCRIPT_CONTAINER_ID = 'tag-header-injected-script-container'
export const SECTION_HEADER_INJECTED_SCRIPT_CONTAINER_ID = 'section-header-injected-script-container'
export const ACCOUNT_INJECTED_SCRIPT_CONTAINER_ID = 'account-injected-script-container'
export const COVER_IMAGE_INJECTED_SCRIPT_CONTAINER_ID = 'cover-image-injected-script-container'
export const THANK_YOU_INJECTED_SCRIPT_CONTAINER_ID = 'thank-you-injected-script-container'
export const SEARCH_INJECTED_SCRIPT_CONTAINER_ID = 'search-injected-script-container'
export const SPOTLIGHT_INJECTED_SCRIPT_CONTAINER_ID = 'spotlight-injected-script-container'
export const DEFAULT_PROMO_CUSTOM_TITLE = 'Unlock Unparalleled Access'
export const DEFAULT_GIFT_PROMO_CUSTOM_TITLE = ''
export const DEFAULT_PROMO_CUSTOM_POPUP_TIME_IN_SECONDS = 30
export const DEFAULT_GIFT_PROMO_CUSTOM_POPUP_TIME_IN_SECONDS = 30
export const DEFAULT_PROMO_TYPE: BlockPubPromo['modalContentType'] = 'promo'
export const DEFAULT_PROMO_MODAL_COOLDOWN_FOR_NON_SUBSCRIBED_USER = 30
export const DEFAULT_GIFT_MODAL_COOLDOWN_FOR_SUBSCRIBED_USER = 30
export const DEFAULT_GIFT_MODAL_COOLDOWN_FOR_NON_SUBSCRIBED_USER = 30
export const DEFAULT_NEWSLETTER_PROMO_CUSTOM_TITLE = 'Subscribe to our newsletter'
export const DEFAULT_NEWSLETTER_PROMO_SUCCESS_TITLE = "You're subscribed!"
export const DEFAULT_NEWSLETTER_PROMO_CUSTOM_POPUP_TIME_IN_SECONDS = 30
export const DEFAULT_NEWSLETTER_MODAL_COOLDOWN_FOR_NON_SUBSCRIBED_USER = 30
export const DEFAULT_FEED_PUBLISH_STATUS: FeedPublishStatus = 'draft'
export const DEFAULT_FEED_PAYWALL: Paywall = 'Public'
export const DEFAULT_FEED_IS_ACTIVE: boolean = false
export const COLLECTION_SIZE_LIMIT = 500
export const TAG_RELATED_ARTICLES_PER_TAG_OPTIONS = [4, 8] as const
export const DEFAULT_TAG_ARTICLES_PER_RELATED_TAG: (typeof TAG_RELATED_ARTICLES_PER_TAG_OPTIONS)[number] = 4
export const DEFAULT_PLAN_AD_SETTINGS: PlanAdSettings = 'AllAds'
export const DEFAULT_PLAN_PRINT_ONLY: boolean = false
export const DEFAULT_PLAN_OPTIONAL_MONTHLY_STATE: OptionalPriceState = 'Inactive'
export const DEFAULT_PLAN_OPTIONAL_YEARLY_STATE: OptionalPriceState = 'Inactive'
export const DEFAULT_WORKSPACE_WRITING_RULE_TYPE: WritingRuleType = 'Body'
export const DEFAULT_WORKSPACE_WRITING_RULE_ACTIVE: boolean = true
export const DEFAULT_STRIPE_ACCOUNT_TYPE: StripeAccountType = 'publication'
export const DEFAULT_HEADER_CUSTOM_CTA_TYPE: HeaderCustomCtaType = 'LINK'
export const DEFAULT_SHOW_CUSTOM_DONATION_SUBSCRIPTION_PAGE: boolean = false
export const PLAYWIRE_CSS_ATTRIBUTE_KEY: string = 'data-playwire-selector'
export const PLAYWIRE_VERTICAL_AD_ID: string = 'playwire-vertical-ad'
export const PLAYWIRE_VERTICAL_AD_2_ID: string = 'playwire-vertical-ad-2'
export const PLAYWIRE_CHATBOT_PRIMARY_AD_ID: string = 'playwire-chatbot-primary-ad'
export const PLAYWIRE_CHATBOT_SECONDARY_AD_ID: string = 'playwire-chatbot-secondary-ad'
export const PLAYWIRE_HORIZONTAL_PRIMARY_AD_ID: string = 'playwire-horizontal-primary-ad'
export const PLAYWIRE_HORIZONTAL_SECONDARY_AD_ID: string = 'playwire-horizontal-secondary-ad'
export const PLAYWIRE_LEADERBOARD_AD_ID: string = 'playwire-leaderboard-ad'
export const PLAYWIRE_FOOTER_AD_ID: string = 'playwire-footer-ad'
export const PLAYWIRE_CATEOGRY_BOTTOM_AD_ID: string = 'playwire-category-bottom-ad'
export const PLAYWIRE_PUBLIC_NAME_OBJECT: Record<string, string> = {
  [PLAYWIRE_VERTICAL_AD_ID]: 'Playwire Vertical Ad Selector Id',
  [PLAYWIRE_VERTICAL_AD_2_ID]: 'Playwire Vertical Ad 2 Selector Id',
  [PLAYWIRE_CHATBOT_PRIMARY_AD_ID]: 'Playwire Chatbot Primary Ad Selector Id',
  [PLAYWIRE_CHATBOT_SECONDARY_AD_ID]: 'Playwire Chatbot Secondary Ad Selector Id',
  [PLAYWIRE_HORIZONTAL_PRIMARY_AD_ID]: 'Playwire Horizontal Primary Ad Selector Id',
  [PLAYWIRE_HORIZONTAL_SECONDARY_AD_ID]: 'Playwire Horizontal Secondary Ad Selector Id',
  [PLAYWIRE_LEADERBOARD_AD_ID]: 'Playwire Leaderboard Ad Selector Id',
  [PLAYWIRE_FOOTER_AD_ID]: 'Playwire Footer Ad Selector Id',
  [PLAYWIRE_CATEOGRY_BOTTOM_AD_ID]: 'Playwire Category Bottom Ad Selector Id',
}
// PubGen Ads (same behavior as PlayWire, different data attribute and selector ids)
export const PUBGEN_AD_CSS_ATTRIBUTE_KEY: string = 'data-pubgen-ad-selector'
export const PUBGEN_AD_VERTICAL_ID: string = 'pubgen-vertical-ad'
export const PUBGEN_AD_CHATBOT_PRIMARY_ID: string = 'pubgen-chatbot-primary-ad'
export const PUBGEN_AD_CHATBOT_SECONDARY_ID: string = 'pubgen-chatbot-secondary-ad'
export const PUBGEN_AD_HORIZONTAL_PRIMARY_ID: string = 'pubgen-horizontal-primary-ad'
export const PUBGEN_AD_HORIZONTAL_SECONDARY_ID: string = 'pubgen-horizontal-secondary-ad'
export const PUBGEN_AD_LEADERBOARD_ID: string = 'pubgen-leaderboard-ad'
export const PUBGEN_AD_FOOTER_ID: string = 'pubgen-footer-ad'
export const PUBGEN_AD_STICKY_FOOTER_ID: string = 'pubgen-sticky-footer-ad'
export const PUBGEN_AD_ARTICLE_IN_CONTENT_ID: string = 'pubgen-article-in-content-ad'
export const PUBGEN_AD_VIDEO_ID: string = 'pubgen-video-ad'
export const PUBGEN_AD_PUBLIC_NAME_OBJECT: Record<string, string> = {
  [PUBGEN_AD_VERTICAL_ID]: 'PubGen Vertical Ad Selector Id',
  [PUBGEN_AD_CHATBOT_PRIMARY_ID]: 'PubGen Chatbot Primary Ad Selector Id',
  [PUBGEN_AD_CHATBOT_SECONDARY_ID]: 'PubGen Chatbot Secondary Ad Selector Id',
  [PUBGEN_AD_HORIZONTAL_PRIMARY_ID]: 'PubGen Horizontal Primary Ad Selector Id',
  [PUBGEN_AD_HORIZONTAL_SECONDARY_ID]: 'PubGen Horizontal Secondary Ad Selector Id',
  [PUBGEN_AD_LEADERBOARD_ID]: 'PubGen Leaderboard Ad Selector Id',
  [PUBGEN_AD_FOOTER_ID]: 'PubGen Footer Ad Selector Id',
  [PUBGEN_AD_STICKY_FOOTER_ID]: 'PubGen Sticky Footer Ad Selector Id',
  [PUBGEN_AD_ARTICLE_IN_CONTENT_ID]: 'PubGen Article In Content Ad Selector Id',
  [PUBGEN_AD_VIDEO_ID]: 'PubGen Video Ad Selector Id',
}
export const DEFAULT_NUMBER_OF_FREE_ARTICLES_PER_MONTH = 5
export const DEFAULT_NUMBER_OF_ARTICLES_WITH_PAYWALL_WARNING = 3
export const DEFAULT_PAYWALL_TYPE: PaywallType = 'default'
export const DEFAULT_DISABLE_PAYWALL = false
export const DEFAULT_HIDE_SECTION_TOP_STORIES = false
export const DEFAULT_DISABLE_STORY_GENERATION = false
export const DEFAULT_DISABLE_TITLE_GENERATION = false
export const DEFAULT_DISABLE_LEAD_GENERATION = false
export const DEFAULT_DISABLE_CMS_CHATBOT = false
export const DEFAULT_SEO_NOINDEX = false

export const DEFAULT_COLUMN_INCH_METHOD: ColumnInchMethod = 'word'
export const DEFAULT_COLUMN_INCH_WORDS_PER_INCH: number = 30
export const DEFAULT_COLUMN_INCH_CHARACTERS_PER_COLUMN_INCH: number = 172.6375

// Default Section layout style
export const DEFAULT_SECTION_LAYOUT: SectionLayoutStyle = 'lines'
export const DEFAULT_TOP_STORIES_LAYOUT: BlockPubHomeTopStoriesLayout = 'LIST_WITH_SIDEBAR'
export const DEFAULT_ARTICLES_BY_SECTION_LAYOUT: BlockPubArticlesBySectionLayout = 'FOUR_COLUMNS'
export const DEFAULT_ARTICLES_BY_SECTION_SHOW_DESCRIPTION: boolean = false

export const DEFAULT_IMAGE_LINK_OPEN_IN_NEW_TAB = true
export const DEFAULT_STORY_HIDE_IMAGE_BACKGROUND: boolean = false

export type ImageBorderRadius = 'none' | 'slight' | 'full'
export const DEFAULT_IMAGE_BORDER_RADIUS: ImageBorderRadius = 'none'
export const DEFAULT_WEATHER_ADVISORY_IS_VISIBLE = true

export const EXTERNAL_FEED_DEFAULT_DISPLAY: ExternalDisplay = 'default'
export const EXTERNAL_FEED_DEFAULT_PUBLISH_STATUS: FeedPublishStatus = 'draft'
export const EXTERNAL_FEED_DEFAULT_PAYWALL: Paywall = 'Metered'
export const EXTERNAL_FEED_DEFAULT_PARSER_TYPE: ExternalParserType = 'RSS2'
export const EXTERNAL_FEED_DEFAULT_IMG_REQUIRED: boolean = false
export const EXTERNAL_FEED_DEFAULT_ACTIVE: boolean = true

export const DEFAULT_EMAIL_SERVICE_AUTO_TAG_SIGNED_IN = false
export const DEFAULT_EMAIL_SERVICE_AUTO_TAG_SUBSCRIBED = false
export const DEFAULT_SEND_NEWSLETTER_SUBSCRIPTION_EMAIL = true
export const MAILCHIMP_TAG_SIGNED_IN = 'PubGen Sign-ups'
export const MAILCHIMP_TAG_SUBSCRIBED = 'PubGen Subscribed'

export const DEFAULT_SEND_SUBSCRIPTION_LIFECYCLE_EMAILS_TO_CUSTOMER: boolean = true

export const CHATBOT_OPEN_EVENT_NAME = 'chatbot-open'
export const CHATBOT_OPENED_EVENT_NAME = 'chatbot-opened'

// AI Model Configuration Constants
// These configs can be imported and used with OpenAI service functions
// Note: If temperature is set, reasoningEffort must be 'none'
// If temperature is not set, reasoningEffort can be 'none' | 'low' | 'medium' | 'high'

// GPT 5.1 model variants
export type GPTModel = 'gpt-5.1' | 'gpt-5-nano'

// Embedding model types
export type EmbeddingModel = 'text-embedding-ada-002'

// AI Config for nano models (no temperature/top_p support, reasoningEffort can be any value)
export type AIConfigNano = {
  model: 'gpt-5-nano'
  reasoningEffort: 'none' | 'low' | 'medium' | 'high'
  max_completion_tokens: number
}

// AI Config with temperature - reasoningEffort must be 'none' (gpt-5.1 only)
export type AIConfigWithTemperature = {
  model: 'gpt-5.1'
  reasoningEffort: 'low' | 'medium' | 'high'
  max_completion_tokens: number
}

// AI Config without temperature - reasoningEffort can be any value (gpt-5.1 only)
export type AIConfigWithoutTemperature = {
  model: 'gpt-5.1'
  reasoningEffort: 'none' | 'low' | 'medium' | 'high'
  max_completion_tokens: number
}

// Union type for AI Config
export type AIConfig = AIConfigWithTemperature | AIConfigWithoutTemperature | AIConfigNano

// Embedding Config type
export type EmbeddingConfig = {
  model: EmbeddingModel
}

// Lead Generation AI Config
export const AI_CONFIG_LEAD_GENERATION: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'medium',
  max_completion_tokens: 4000,
}

// Headline Generation AI Config
export const AI_CONFIG_HEADLINE_GENERATION: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'medium',
  max_completion_tokens: 6000,
}

// Article Body Generation AI Config
export const AI_CONFIG_ARTICLE_BODY: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'medium',
  max_completion_tokens: 32768,
}

export const AI_CONFIG_SECTION_GENERATION: AIConfigNano = {
  model: 'gpt-5-nano',
  reasoningEffort: 'low',
  max_completion_tokens: 500,
}

export const AI_CONFIG_TAG_GENERATION: AIConfigNano = {
  model: 'gpt-5-nano',
  reasoningEffort: 'low',
  max_completion_tokens: 1000,
}

// Chatbot CMS AI Config
export const AI_CONFIG_CHATBOT_CMS: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'low',
  max_completion_tokens: 16384,
}

// Chatbot Public Site AI Config
export const AI_CONFIG_CHATBOT_PUBLIC: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'low',
  max_completion_tokens: 10000,
}

// Query Analysis AI Config (for embeddings search)
export const AI_CONFIG_QUERY_ANALYSIS: AIConfigWithTemperature = {
  model: 'gpt-5.1',
  reasoningEffort: 'medium',
  max_completion_tokens: 30000,
}

// Lead Generation for Imported Articles AI Config
export const AI_CONFIG_LEAD_FOR_IMPORT: AIConfigNano = {
  model: 'gpt-5-nano',
  reasoningEffort: 'low',
  max_completion_tokens: 6000,
}

// Embeddings Model Config (for vector embeddings)
export const AI_CONFIG_EMBEDDINGS: EmbeddingConfig = {
  model: 'text-embedding-ada-002',
}

// ============================================================================
// Consent Management Constants
// ============================================================================
// NOTE: These values must also be kept in sync with the CloudFront Function
// in stacks/next-public-site.ts (which cannot import from this file)

/**
 * Consent region codes used for privacy regulation compliance.
 * These are set by CloudFront based on visitor geolocation.
 */
export const CONSENT_REGIONS = {
  /** European Union / EEA - GDPR applies, opt-in required */
  EU: 'EU',
  /** California, USA - CCPA/CPRA applies, opt-out model */
  US_CALIFORNIA: 'US-CA',
  /** Quebec, Canada - Law 25 applies, opt-in required */
  CANADA_QUEBEC: 'CA-QC',
  /** Rest of USA - no specific regulation */
  US: 'US',
  /** Rest of Canada - PIPEDA applies, less strict */
  CANADA: 'CA',
  /** Rest of World - no specific regulation */
  REST_OF_WORLD: 'ROW',
} as const

export type ConsentRegion = (typeof CONSENT_REGIONS)[keyof typeof CONSENT_REGIONS]

/**
 * EU/EEA country codes for GDPR compliance.
 * Includes EU member states, EEA countries, and UK.
 */
export const EU_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'IS',
  'LI',
  'NO',
] as const

/**
 * Available print-on-demand shipping destinations (ISO-3166 alpha-2 codes).
 * Newsrooms pick a subset in CMS; the Stripe checkout session limits shipping to the chosen codes.
 * Extend this list as the provider (Gelato) onboards additional supported destinations.
 */
export const PRINT_ON_DEMAND_SHIPPING_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
] as const

/**
 * Fraction of the **newsroom markup** that PubGen keeps as a platform fee.
 * Applied via Stripe Connect `application_fee_amount` on every print-on-demand
 * checkout session — the Gelato cost itself flows untouched to the newsroom
 * Connect account; PubGen only takes a slice of the margin the newsroom adds
 * on top. Read in `services/stripe.service.ts` and the
 * `/api/gelato/get-price` route. Change here to roll out a new fee rate to
 * every stage at once.
 *
 * Money flow per print order:
 *   buyer pays:           cost x (1 + markup)
 *   PubGen receives:      cost x markup x PRINT_ON_DEMAND_PLATFORM_FEE_PERCENT
 *   newsroom Connect:     cost + cost x markup x (1 - PRINT_ON_DEMAND_PLATFORM_FEE_PERCENT)
 *   newsroom net (after paying Gelato cost):  markup x (1 - PRINT_ON_DEMAND_PLATFORM_FEE_PERCENT)
 */
export const PRINT_ON_DEMAND_PLATFORM_FEE_PERCENT = 0.1

/**
 * Default retail markup the newsroom adds on top of Gelato's cost when no
 * per-site override is set on `Site.gelatoNewsroomMarkupPercent`. 40% lines up
 * with typical print-on-demand industry retail margin: on a $15 Gelato cost it
 * yields a $21 buyer-facing total and a $5.40 newsroom net per item.
 *
 * Newsrooms can override (including 0% for cost-passthrough nonprofits) on the
 * CMS Print on Demand settings page. Read in `services/stripe.service.ts` and
 * the `/api/gelato/get-price` route; both fall back to this constant when the
 * site has not persisted a value.
 */
export const PRINT_ON_DEMAND_DEFAULT_NEWSROOM_MARKUP_PERCENT = 0.4

/**
 * Gelato Order API base URL (https://dashboard.gelato.com/docs/orders/v4/).
 * Used to create orders, fetch quotes, and look up an order by id during the
 * webhook recovery path in `functions/admin/gelatoWebhook.ts`.
 */
export const GELATO_ORDER_API_BASE_URL = 'https://order.gelatoapis.com/v4'

/** Cookie name for storing consent preferences */
export const CONSENT_COOKIE_NAME = 'x-consent'

/** Cookie name for geo region (set by CloudFront) */
export const GEO_REGION_COOKIE_NAME = 'x-geo-region'

/** localStorage key for consent backup storage */
export const CONSENT_STORAGE_KEY = 'pubgen_consent_state'

/**
 * Cookie name for App Tracking Transparency (ATT) status.
 *
 * Set by the native iOS / Android app on the WebView's cookie store BEFORE
 * loading the public site URL. When the value is 'denied', the public site
 * MUST NOT enable any third-party advertising / cross-site tracking, even if
 * the saved consent state would otherwise allow it.
 *
 * Apple's App Tracking Transparency policy treats third-party advertising
 * (Google Ad Manager, etc.) as "tracking", so a denied ATT response makes
 * those scripts a hard no regardless of the user's cookie banner choice.
 *
 * Values: see `AttStatus` below.
 */
export const ATT_STATUS_COOKIE_NAME = 'x-att-status'

/**
 * App Tracking Transparency status values written to the ATT cookie.
 *
 * - `granted`  — User tapped "Allow Tracking" in the iOS ATT prompt (or the
 *   Android equivalent). Full CMP behaviour applies.
 * - `denied`   — User tapped "Ask App Not To Track". Ads category is force-
 *   disabled and the "Advertising Cookies" toggle is hidden in the CMP.
 * - `unknown`  — App couldn't determine the status (or it's not yet been
 *   answered). Treated like a regular browser session (no override).
 */
export const ATT_STATUS_VALUES = {
  GRANTED: 'granted',
  DENIED: 'denied',
  UNKNOWN: 'unknown',
} as const

export type AttStatus = (typeof ATT_STATUS_VALUES)[keyof typeof ATT_STATUS_VALUES]

// Google Maps Embed API Key (restricted to Maps Embed API only)
// Replace with your actual Google Maps Embed API key from Google Cloud Console
export const GOOGLE_MAPS_EMBED_API_KEY = 'AIzaSyAZP8IPYz4m3tU7MAXLYHiWRz2RQr7eBYo' // Free restricted API Key to the Google Maps Embed Service
